import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar } from 'lucide-react';

const HistoricalPerformance = () => {
  const [timeRange, setTimeRange] = useState('24h');

  // Generate historical data
  const generateHistoricalData = () => {
    const dataPoints = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
    const data = [];
    
    for (let i = 0; i < dataPoints; i++) {
      const baseValue = 450;
      data?.push({
        timestamp: timeRange === '24h' 
          ? `${i}:00`
          : timeRange === '7d'
          ? `Day ${Math.floor(i / 24) + 1}`
          : `Week ${Math.floor(i / 168) + 1}`,
        totalVolume: baseValue + Math.floor(Math.random() * 200),
        errorRate: Math.random() * 5,
        latency: 200 + Math.random() * 300,
        reliability: 95 + Math.random() * 5
      });
    }
    
    return data;
  };

  const historicalData = generateHistoricalData();

  const peakUsageHours = [
    { hour: '09:00-11:00', volume: 1250, reason: 'Morning engagement peak' },
    { hour: '13:00-15:00', volume: 980, reason: 'Lunch break activity' },
    { hour: '19:00-21:00', volume: 1450, reason: 'Evening peak hours' }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Historical Performance Tracking</h2>
          <p className="text-sm text-gray-600 mt-1">
            Collection trends, peak patterns, and system reliability metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Calendar className="w-5 h-5 text-blue-600" />
        </div>
      </div>
      {/* Volume Trend Chart */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Collection Volume Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={historicalData}>
            <defs>
              <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tick={{ fontSize: 12 }}
              interval={timeRange === '24h' ? 2 : timeRange === '7d' ? 1 : 'preserveStartEnd'}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="totalVolume" 
              stroke="#3B82F6" 
              fillOpacity={1} 
              fill="url(#colorVolume)"
              name="Total Volume"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Reliability Metrics */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">System Reliability</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={historicalData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tick={{ fontSize: 12 }}
              interval={timeRange === '24h' ? 2 : timeRange === '7d' ? 1 : 'preserveStartEnd'}
            />
            <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="reliability" 
              stroke="#10B981" 
              strokeWidth={2}
              dot={false}
              name="Reliability %"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Error Rate Trend */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Error Rate Trend</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={historicalData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tick={{ fontSize: 12 }}
              interval={timeRange === '24h' ? 2 : timeRange === '7d' ? 1 : 'preserveStartEnd'}
            />
            <YAxis tick={{ fontSize: 12 }} domain={[0, 10]} />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="errorRate" 
              stroke="#EF4444" 
              strokeWidth={2}
              dot={false}
              name="Error Rate %"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Peak Usage Patterns */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-4">Peak Usage Patterns</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {peakUsageHours?.map((peak, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-900">{peak?.hour}</span>
              </div>
              <p className="text-2xl font-bold text-blue-600 mb-1">
                {peak?.volume} <span className="text-sm font-normal text-gray-600">items/hr</span>
              </p>
              <p className="text-xs text-gray-600">{peak?.reason}</p>
            </div>
          ))}
        </div>
      </div>
      {/* System Statistics */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {(historicalData?.reduce((sum, d) => sum + d?.totalVolume, 0) / historicalData?.length)?.toFixed(0)}
            </p>
            <p className="text-xs text-gray-600">Avg Volume</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {(historicalData?.reduce((sum, d) => sum + d?.reliability, 0) / historicalData?.length)?.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-600">Avg Reliability</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">
              {(historicalData?.reduce((sum, d) => sum + d?.errorRate, 0) / historicalData?.length)?.toFixed(2)}%
            </p>
            <p className="text-xs text-gray-600">Avg Error Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">
              {(historicalData?.reduce((sum, d) => sum + d?.latency, 0) / historicalData?.length)?.toFixed(0)}ms
            </p>
            <p className="text-xs text-gray-600">Avg Latency</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalPerformance;